import type React from 'react';
import {
    BookOpen, Truck, Clock, FileText, CheckSquare, Shield,
    RotateCcw, Zap, Network, UserCheck, Activity, Stamp,
} from 'lucide-react';
import type { BlockType } from '../types';

export interface DesignCardStyle {
    bg: string;
    border: string;
    hoverBorder: string;
    badgeBg: string;
    dotColor: string;
}

export interface BlockTypeEntry {
    value: BlockType;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    iconColor: string;
    showInDropdown: boolean;
    designCard?: DesignCardStyle;
}

export const BLOCK_TYPE_REGISTRY: Record<BlockType, BlockTypeEntry> = {
    theory:        { value: 'theory',        label: 'TrainingRoom Prerequisite',  icon: BookOpen,    iconColor: 'text-blue-500',      showInDropdown: true },
    ojt:           { value: 'ojt',           label: 'On the Job Training',        icon: UserCheck,   iconColor: 'text-green-600',     showInDropdown: true,
                     designCard: { bg: 'bg-green-50', border: 'border-green-300', hoverBorder: 'hover:border-green-500', badgeBg: 'bg-aero-900', dotColor: 'bg-aero-300' } },
    practical:     { value: 'practical',     label: 'Practical',                  icon: Truck,       iconColor: 'text-orange-500',    showInDropdown: true },
    assessment:    { value: 'assessment',    label: 'Assessment',                 icon: Shield,      iconColor: 'text-red-500',       showInDropdown: true },
    upload:        { value: 'upload',        label: 'Upload',                     icon: FileText,    iconColor: 'text-gray-500',      showInDropdown: true },
    api:           { value: 'api',           label: '3rd Party API',              icon: Zap,         iconColor: 'text-cyan-500',      showInDropdown: true },
    pathway:       { value: 'pathway',       label: 'Sub-Pathway',                icon: Network,     iconColor: 'text-emerald-600',   showInDropdown: true,
                     designCard: { bg: 'bg-emerald-50', border: 'border-emerald-300', hoverBorder: 'hover:border-emerald-500', badgeBg: 'bg-emerald-600', dotColor: 'bg-emerald-400' } },
    authorisation: { value: 'authorisation', label: 'Official Authorisation',     icon: Stamp,       iconColor: 'text-amber-600',     showInDropdown: true,
                     designCard: { bg: 'bg-amber-50', border: 'border-amber-300', hoverBorder: 'hover:border-amber-500', badgeBg: 'bg-amber-600', dotColor: 'bg-amber-400' } },
    integration:   { value: 'integration',   label: 'Integration',                icon: BookOpen,    iconColor: 'text-blue-500',      showInDropdown: false },
    milestone:     { value: 'milestone',     label: 'Milestone',                  icon: Clock,       iconColor: 'text-purple-500',    showInDropdown: false },
    affidavit:     { value: 'affidavit',     label: 'Affidavit',                  icon: CheckSquare, iconColor: 'text-indigo-500',    showInDropdown: false },
    coaching:      { value: 'coaching',      label: 'Coaching',                   icon: RotateCcw,   iconColor: 'text-yellow-600',    showInDropdown: false },
};

export function renderBlockIcon(type: BlockType | string, sizeClass = 'w-4 h-4'): React.ReactElement {
    const entry = BLOCK_TYPE_REGISTRY[type as BlockType];
    if (!entry) return <Activity className={`${sizeClass} text-aero-400`} />;
    const Icon = entry.icon;
    return <Icon className={`${sizeClass} ${entry.iconColor}`} />;
}

export function getBlockTypeLabel(type: BlockType | string): string {
    if (type === 'api') return '3rd Party';
    return BLOCK_TYPE_REGISTRY[type as BlockType]?.label ?? type;
}

export function getBlockTypeDisplayName(type: BlockType | string): string {
    if (type === 'api') return '3rd Party';
    return type;
}

export function getDropdownBlockTypes(): Array<{ value: BlockType; label: string }> {
    return Object.values(BLOCK_TYPE_REGISTRY)
        .filter(e => e.showInDropdown)
        .map(e => ({ value: e.value, label: e.label }));
}

export function getDesignCardStyle(type: BlockType | string): DesignCardStyle | null {
    return BLOCK_TYPE_REGISTRY[type as BlockType]?.designCard ?? null;
}
